"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { CustomerAccessForm } from "./CustomerAccessForm";
import { SpeedTestGuide } from "./SpeedTestGuide";

type SafeContract = {
  id: string; status: string; plano: string; vencimento: string;
  cidade?: string; nome?: string; clienteId?: number;
};
type ClientData = { documento: string; cpfcnpj?: string; contratos: SafeContract[]; };
type HistoryEntry = { date: string; download: string; upload: string; ping: string; };

type Titulo = {
  id: number;
  numeroDocumento: number;
  status: string;
  valor: string;
  dataVencimento: string;
  dataPagamento: string;
  link: string;
  link_cobranca: string;
  linhaDigitavel: string;
  codigoPix: string;
  pago: boolean;
};

const company = {
  whatsapp: "12 3893-1530",
  email: "contato@housenet.net.br",
  site: "www.housenet.com.br",
  coverage: "São Sebastião, Ilhabela, Caraguatatuba e Ubatuba - SP",
};

const INITIAL_HISTORY: HistoryEntry[] = [
  { date: "Hoje",  download: "487 Mbps", upload: "212 Mbps", ping: "8 ms" },
  { date: "Ontem", download: "501 Mbps", upload: "221 Mbps", ping: "7 ms" },
  { date: "15/07", download: "476 Mbps", upload: "205 Mbps", ping: "9 ms" },
];

/* ── HEADER ── */
function Header({ onLogout, authenticated }: { onLogout?: () => void; authenticated: boolean }) {
  return (
    <header className="hn-header">
      <div className="hn-header-left">
        <div style={{ position: "relative" }}>
          <Image
            src="/housenet-logo-completa.png"
            alt="Housenet"
            width={160}
            height={50}
            style={{ objectFit: "contain", filter: "brightness(0) invert(1)", width: "auto", height: 50 }}
            priority
          />
        </div>
        <div>
          <p className="hn-logo-label">Portal do Cliente</p>
          <p className="hn-subtitle">{company.coverage}</p>
        </div>
      </div>
      <div className="hn-header-right">
        <div className="hn-contact">
          <a href={`https://${company.site}`}>{company.site}</a>
          <span>WhatsApp: {company.whatsapp}</span>
          <span>{company.email}</span>
        </div>
        {authenticated && onLogout && (
          <button className="hn-logout-btn" onClick={onLogout}>↩ Sair</button>
        )}
      </div>
    </header>
  );
}

/* ── LOGIN ── */
function LoginScreen({ onSuccess }: { onSuccess: (data: ClientData) => void }) {
  return (
    <div className="hn-login-wrap">
      <div
        className="hn-login-card"
        style={{
          maxWidth: 420,
          background: "rgba(13,20,34,.75)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(37,99,235,.35)",
          borderRadius: 24,
          boxShadow: "0 0 50px rgba(37,99,235,.1), inset 0 1px 0 rgba(255,255,255,.06)",
          position: "relative",
          overflow: "hidden",
          textAlign: "center",
          padding: "36px 28px",
        }}
      >
        {/* Borda brilhante no topo */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 1,
          background: "linear-gradient(90deg,transparent,rgba(37,99,235,.7),rgba(96,165,250,.7),transparent)",
        }} />

        {/* Glows */}
        <div style={{ position:"absolute",top:-40,right:-40,width:180,height:180,background:"rgba(37,99,235,.2)",borderRadius:"50%",filter:"blur(40px)",pointerEvents:"none" }} />
        <div style={{ position:"absolute",bottom:-30,left:-20,width:130,height:130,background:"rgba(37,99,235,.15)",borderRadius:"50%",filter:"blur(35px)",pointerEvents:"none" }} />

        <div style={{ position: "relative" }}>
          {/* Logo */}
          <div style={{ display:"flex", justifyContent:"center", marginBottom: 16 }}>
            <Image
              src="/housenet-logo-completa.png"
              alt="Housenet"
              width={200}
              height={64}
              style={{ objectFit:"contain", filter:"brightness(0) invert(1)", maxWidth:"100%" }}
              priority
            />
          </div>

          {/* Descrição */}
          <p style={{ fontSize:13, color:"var(--muted)", lineHeight:1.6, marginBottom: 22 }}>
            Acesse seu contrato, segunda via de boleto e informações da sua conexão.
          </p>

          {/* Formulário */}
          <CustomerAccessForm onSuccess={onSuccess} />
        </div>
      </div>

      {/* Chips */}
      <div className="hn-chips">
        {[
          { ico: "🔒", label: "Acesso seguro por CPF/CNPJ" },
          { ico: "📄", label: "Consulta de contrato" },
          { ico: "💳", label: "Segunda via de boleto" },
        ].map((item) => (
          <div key={item.label} className="hn-chip">
            <span>{item.ico}</span><span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── DASHBOARD ── */
function Dashboard({ client }: { client: ClientData }) {
  const contrato = client.contratos[0];
  const [speedHistory, setSpeedHistory] = useState<HistoryEntry[]>(INITIAL_HISTORY);
  void speedHistory; void setSpeedHistory;
  const [titulos, setTitulos] = useState<Titulo[]>([]);
  const [loadingTitulos, setLoadingTitulos] = useState(true);
  const [copiado, setCopiado] = useState<number | null>(null);

  useEffect(() => {
    if (!contrato?.id) return;
    setLoadingTitulos(true);
    fetch("/api/sgp/titulos", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contrato_id: Number(contrato.id),
        cpfcnpj: client.cpfcnpj,
      }),
    })
      .then((r) => r.json())
      .then((j) => setTitulos(j.data?.titulos ?? []))
      .catch(() => setTitulos([]))
      .finally(() => setLoadingTitulos(false));
  }, [contrato?.id, client.cpfcnpj]);

  // Extrai primeiro nome do titular
  const primeiroNome = contrato?.nome
    ? contrato.nome.split(" ")[0].charAt(0).toUpperCase() +
      contrato.nome.split(" ")[0].slice(1).toLowerCase()
    : null;

  function copiarCodigo(titulo: Titulo) {
    const codigo = titulo.linhaDigitavel || titulo.codigoPix;
    if (!codigo) return;
    navigator.clipboard.writeText(codigo).then(() => {
      setCopiado(titulo.id);
      setTimeout(() => setCopiado(null), 2000);
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── BOAS-VINDAS ── */}
      <div className="hn-welcome">
        <p style={{ fontSize: 28, fontWeight: 900, color: "var(--text)", letterSpacing: "-.4px" }}>
          {primeiroNome ? `Bem-vindo, ${primeiroNome}! 👋` : "Bem-vindo! 👋"}
        </p>
        <p style={{ fontSize: 11, color: "var(--muted)", marginTop: 6 }}>
          {client.documento}
        </p>
      </div>

      {/* ── PLANO ── */}
      <div className="hn-card" style={{
        background: "#0d1626",
        position: "relative", overflow: "hidden",
      }}>
        {/* glow */}
        <div style={{ position:"absolute",top:-40,left:-40,width:160,height:160,background:"var(--primary-glow)",borderRadius:"50%",filter:"blur(40px)",pointerEvents:"none" }} />
        <div style={{ position:"absolute",bottom:-30,right:-20,width:120,height:120,background:"var(--blue-glow)",borderRadius:"50%",filter:"blur(35px)",pointerEvents:"none" }} />

        <div style={{ position:"relative", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
          <div>
            <p style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".8px", color:"var(--muted)", marginBottom:6 }}>
              Meu Plano
            </p>
            <p style={{ fontSize:22, fontWeight:900, color:"var(--text)", letterSpacing:"-.3px", lineHeight:1 }}>
              {contrato?.plano ?? "—"}
            </p>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:10, flexWrap:"wrap" }}>
              <span className="hn-badge-green">{contrato?.status ?? "Ativo"}</span>
              <span style={{ fontSize:12, color:"var(--muted)" }}>
                Vencimento: <strong style={{ color:"var(--text)" }}>{contrato?.vencimento ?? "—"}</strong>
              </span>
              <span style={{ fontSize:12, color:"var(--muted)" }}>
                Cidade: <strong style={{ color:"var(--text)" }}>{contrato?.cidade ?? "—"}</strong>
              </span>
            </div>
          </div>
          <div style={{ textAlign:"right" }}>
            <p style={{ fontSize:11, color:"var(--muted)", marginBottom:4 }}>Contrato</p>
            <p style={{ fontSize:16, fontWeight:800, color:"var(--text)" }}>#{contrato?.id ?? "—"}</p>
          </div>
        </div>
      </div>

      {/* ── GRID ── */}
      <div className="hn-grid-2">

        {/* Boletos */}
        <div className="hn-card">
          <p className="hn-card-title">2ª Via de Boleto</p>
          {loadingTitulos ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "var(--muted)", fontSize: 13 }} role="status" aria-live="polite">
              Carregando boletos...
            </div>
          ) : titulos.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "var(--muted)", fontSize: 13 }}>
              Nenhum boleto encontrado.
            </div>
          ) : (
            titulos.map((t) => (
              <div key={t.id} className="hn-boleto">
                <div className="hn-boleto-top">
                  <div>
                    <p className="hn-boleto-id">Nº {t.numeroDocumento}</p>
                    <p className="hn-boleto-amt">{t.valor}</p>
                    <p className="hn-boleto-due">Vencimento: {t.dataVencimento}</p>
                    {t.pago && t.dataPagamento && (
                      <p style={{ fontSize: 11, color: "#34d399", marginTop: 2 }}>Pago em {t.dataPagamento}</p>
                    )}
                  </div>
                  <span className={t.pago ? "hn-badge-green" : "hn-badge-orange"}>
                    {t.pago ? "Pago" : "Em aberto"}
                  </span>
                </div>
                <div className="hn-boleto-btns">
                  <a
                    href={t.link_cobranca || t.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hn-btn-outline"
                    style={{ height: 40, padding: "0 12px", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
                  >
                    {t.pago ? "Comprovante" : "Ver boleto"}
                  </a>
                  {!t.pago && (
                    <button
                      className="hn-btn-secondary"
                      onClick={() => copiarCodigo(t)}
                    >
                      {copiado === t.id ? "✅ Copiado!" : "Copiar código"}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Speed Test */}
        <div className="hn-card">
          <p className="hn-card-title">Teste de Velocidade</p>
          <div style={{ marginTop: 4 }}>
            <SpeedTestGuide />
          </div>
        </div>
      </div>

      {/* Fale conosco + Campanhas */}
      <div className="hn-grid-3">

        {/* Fale conosco */}
        <div className="hn-contact-card">
          <div className="hn-contact-ico" style={{ background: "rgba(37,99,235,.15)", border: "1px solid rgba(37,99,235,.25)" }} role="img" aria-label="Fale conosco">💬</div>
          <h3 className="hn-contact-title">Fale conosco</h3>
          <p className="hn-contact-desc">Atendimento rápido pelo WhatsApp, telefone ou canais oficiais da Housenet.</p>
          <div className="hn-channel-list">
            {[
              { ico: "📱", name: "WhatsApp",   sub: "Resposta em até 5 min", href: "https://api.whatsapp.com/send?phone=551238931530&text=Ol%C3%A1" },
              { ico: "📞", name: "Telefone",   sub: `${company.whatsapp} · 24h`, href: `tel:${company.whatsapp.replace(/\D/g, "")}` },
              { ico: "🌐", name: "Portal Web", sub: company.site, href: `https://${company.site}` },
            ].map((ch) => (
              <a key={ch.name} href={ch.href} target="_blank" rel="noopener noreferrer" className="hn-channel" style={{ textDecoration: "none" }}>
                <span className="hn-channel-ico">{ch.ico}</span>
                <div>
                  <p className="hn-channel-name">{ch.name}</p>
                  <p className="hn-channel-sub">{ch.sub}</p>
                </div>
                <span className="hn-channel-arr">›</span>
              </a>
            ))}
          </div>
          <a
            href="https://api.whatsapp.com/send?phone=551238931530&text=Ol%C3%A1"
            target="_blank"
            rel="noopener noreferrer"
            className="hn-btn-contact"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
          >
            💬 Entrar em contato
          </a>
        </div>

        {/* Campanhas */}
        <div className="hn-camp-card">
          <div className="hn-contact-ico" style={{ background: "rgba(37,99,235,.15)", border: "1px solid rgba(37,99,235,.25)" }} role="img" aria-label="Campanhas">📢</div>
          <h3 className="hn-contact-title">Campanhas temporárias</h3>
          <p className="hn-contact-desc">As promoções aparecem apenas em períodos definidos e poderão ser enviadas por push para clientes elegíveis.</p>
          <div className="hn-camp-tags">
            {["Promoções por período", "Avisos de vencimento", "Push segmentado"].map((item) => (
              <div key={item} className="hn-camp-tag">{item}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── APP SHELL ── */
export function AppShell() {
  const [client, setClient] = useState<ClientData | null>(null);
  return (
    <main className="hn-main">
      <div className="hn-inner">
        <Header onLogout={() => setClient(null)} authenticated={!!client} />
        {client ? <Dashboard client={client} /> : <LoginScreen onSuccess={setClient} />}
      </div>
    </main>
  );
}
